import type {OutlookRecipient} from './EmailScreen/Content/EmailRecipients/useOutlookEmailRecipients';
import type {ReadMessageMetadata} from './outlookReadItem';
import type {RoamingSettingsState} from './RoamingSettingsContext';
import {
  createDialogSessionToken,
  type DialogMessagePayload,
  getParentDialogToken,
  parseDialogMessage,
  parseParentRoamingSettingsMessage,
  parseRoamingSettingsUpdatedMessage,
  type RoamingSettingsUpdate,
  SEND_DIALOG_PARENT_TOKEN_PARAM,
  stringifyDialogMessage,
} from './sendDialogProtocol';
import type {
  CompanyLink,
  DealLink,
  JobLink,
  MessageAttachment,
  PersonLink,
} from './SendModal/EmailSync/structs';

export {
  createDialogSessionToken,
  getParentDialogToken,
  parseDialogMessage,
  stringifyDialogMessage,
};
export type {DialogMessagePayload, RoamingSettingsUpdate};

export type {ReadMessageMetadata};

export type IncomingSyncSelection = {
  agencyId: number;
  personLinks: PersonLink[];
  companyLinks: CompanyLink[];
  jobLinks: JobLink[];
  dealLinks: DealLink[];
};

type ParentReadMessageMetadataMessage = {
  type: 'readMessageMetadata';
  metadata: ReadMessageMetadata;
};

type ParentRoamingSettingsMessage = {
  type: 'roamingSettings';
  settings: RoamingSettingsState;
};

type RoamingSettingsUpdatedMessage = {
  type: 'roamingSettingsUpdated';
  ok: boolean;
  settings?: RoamingSettingsState;
  error?: string;
};

type IncomingSyncCreatedMessage = {
  type: 'incomingSyncCreated';
  ok: boolean;
  errors?: string[];
  error?: string;
};

const isOutlookRecipient = (value: unknown): value is OutlookRecipient => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const recipient = value as OutlookRecipient;
  return (
    typeof recipient.email === 'string' &&
    (recipient.field === 'to' ||
      recipient.field === 'cc' ||
      recipient.field === 'bcc' ||
      recipient.field === 'from')
  );
};

const isMessageAttachment = (value: unknown): value is MessageAttachment => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const attachment = value as MessageAttachment;
  return typeof attachment.name === 'string' && attachment.name.trim() !== '';
};

export const isReadMessageMetadata = (
  value: unknown
): value is ReadMessageMetadata => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const metadata = value as ReadMessageMetadata;
  return (
    typeof metadata.fromAddress === 'string' &&
    typeof metadata.subject === 'string' &&
    Array.isArray(metadata.recipients) &&
    metadata.recipients.every(isOutlookRecipient) &&
    (metadata.internetMessageId === null ||
      typeof metadata.internetMessageId === 'string') &&
    (metadata.microsoftMessageId === null ||
      typeof metadata.microsoftMessageId === 'string') &&
    (metadata.microsoftConversationId === null ||
      typeof metadata.microsoftConversationId === 'string') &&
    (metadata.sentAt === null || typeof metadata.sentAt === 'string') &&
    (metadata.inboxEmailAddress === null ||
      typeof metadata.inboxEmailAddress === 'string') &&
    Array.isArray(metadata.attachments) &&
    metadata.attachments.every(isMessageAttachment)
  );
};

export const inboundSyncDialogUrl = (token: string): string => {
  const url = new URL('/inbound-sync-dialog.html', window.location.origin);
  const hashParams = new URLSearchParams();
  hashParams.set(SEND_DIALOG_PARENT_TOKEN_PARAM, token);
  url.hash = hashParams.toString();
  return url.toString();
};

export const parseParentReadMessageMetadataMessage = (
  raw: string
): ReadMessageMetadata | null => {
  const message = parseDialogMessage(
    raw
  ) as ParentReadMessageMetadataMessage | null;
  if (
    message?.type !== 'readMessageMetadata' ||
    !isReadMessageMetadata(message.metadata)
  ) {
    return null;
  }
  return message.metadata;
};

const isIncomingSyncSelection = (
  value: unknown
): value is IncomingSyncSelection => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const selection = value as IncomingSyncSelection;
  return (
    typeof selection.agencyId === 'number' &&
    Array.isArray(selection.personLinks) &&
    Array.isArray(selection.companyLinks) &&
    Array.isArray(selection.jobLinks) &&
    Array.isArray(selection.dealLinks)
  );
};

export const parseIncomingSyncCreatedMessage = (
  raw: string
): IncomingSyncCreatedMessage | null => {
  const message = parseDialogMessage(raw) as IncomingSyncCreatedMessage | null;
  if (
    message?.type !== 'incomingSyncCreated' ||
    typeof message.ok !== 'boolean'
  ) {
    return null;
  }
  return message;
};

export const parseCreateIncomingSyncSelection = (
  message: DialogMessagePayload
): IncomingSyncSelection | null => {
  if (message.type !== 'createIncomingSync') {
    return null;
  }
  return isIncomingSyncSelection(message.selection) ? message.selection : null;
};

export {parseParentRoamingSettingsMessage, parseRoamingSettingsUpdatedMessage};

export type {
  IncomingSyncCreatedMessage,
  ParentRoamingSettingsMessage,
  RoamingSettingsUpdatedMessage,
};
